/**
 * Clickref builders — the attribution token Lemon hands to affiliate networks.
 *
 * Two formats coexist:
 * - the historical positional one, `pid__gclid__msclkid__extra` (Google, Bing);
 * - the v3 one, `v3_` + base64url of `key=value;…` (Facebook, ChatGPT Ads).
 *
 * Both are parsed back by Traack (AffiliateNetwork::decoupeSub /
 * decodeAndParseV3ClickId) when affiliate commissions are imported, so the
 * layouts below are a contract with that repo, not a local convention.
 */

type V3KnownKey = 'pid' | 'fbclid' | 'oppref' | 'site';

export type ClickrefV3Params = Partial<Record<V3KnownKey, string | string[]>>;

/**
 * Build the historical clickref from gclid/msclkid parameters.
 */
export function buildClickref(
  pageId: string,
  params: URLSearchParams,
  cookies: Record<string, string>
): string {
  let gclidValue = cookies['gclid'] || params.get('gclid') || '';
  let msclkidValue = cookies['msclkid'] || params.get('msclkid') || '';
  let additionalParam = '';

  // gbraid/wbraid as fallback for gclid
  if (!gclidValue && params.has('gbraid')) {
    gclidValue = params.get('gbraid')!;
    additionalParam += 'gbraid';
  }
  if (!gclidValue && params.has('wbraid')) {
    gclidValue = params.get('wbraid')!;
    additionalParam += 'wbraid';
  }

  // Additional parameters
  if (params.has('gad_source')) additionalParam += '-gad';
  if (params.has('cn')) additionalParam += '-' + params.get('cn');

  // Default values
  if (!gclidValue) gclidValue = 'gclid_value';
  if (!msclkidValue) msclkidValue = 'msclkid_value';

  return `${pageId}__${gclidValue}__${msclkidValue}__${additionalParam}`;
}

/**
 * Create clickref v3 — `v3_` + base64url of `key=value` pairs joined by `;`.
 */
export function createClickRefV3(params: ClickrefV3Params): string {
  const encodeBase64Url = (str: string) =>
    btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

  const pairs: ClickrefV3Params = { ...params };
  if (!pairs.site) pairs.site = 'lem';

  const values = Object.entries(pairs).map(([key, value]) => {
    const encodedValue = Array.isArray(value) ? value.join(';') : value;
    return `${key}=${encodedValue}`;
  });

  return 'v3_' + encodeBase64Url(values.join(';'));
}

/**
 * Whether an attribution id survives a clickref round-trip untouched.
 *
 * `;` and `=` are the pair separators Traack's decoder splits on, so a value
 * carrying one is dropped or truncated at commission import. Outside printable
 * ASCII, btoa() either throws (above U+00FF, breaking hydration on a crafted
 * landing URL) or emits single latin-1 bytes that are not valid UTF-8 once
 * base64-decoded on the PHP side.
 */
export function isTransportableOppref(oppref: string): boolean {
  if (oppref.includes(';') || oppref.includes('=')) return false;

  return /^[\u0020-\u007e]+$/.test(oppref);
}

/**
 * Build the clickref carrying a ChatGPT Ads attribution id.
 *
 * `oppref` reaches Traack through the v3 clickref only — it has no slot in the
 * positional format — and OpenAI requires the original value, so it is carried
 * as-is: no encoding, hashing or truncating.
 *
 * Returns null on a value the format cannot carry: no attribution beats a
 * corrupted one.
 */
export function buildOpprefClickref(pageId: string, oppref: string): string | null {
  if (!isTransportableOppref(oppref)) return null;

  return createClickRefV3({ pid: pageId, oppref });
}

export interface ClickrefWrites {
  /** Cookies to persist, by name. */
  cookies: Record<string, string>;
  /** Facebook needs the _fbc cookie its pixel writes, so it is polled for. */
  facebookTracking: boolean;
}

/**
 * Decide what a page view writes, from its URL parameters and current cookies.
 *
 * Channel order is a precedence: Google/Bing first, then ChatGPT Ads, then
 * Facebook. An unusable oppref falls through instead of swallowing the branch,
 * otherwise a stale one would leave a live Facebook click unattributed.
 */
export function resolveClickrefWrites(
  pageId: string,
  params: URLSearchParams,
  cookies: Record<string, string>
): ClickrefWrites {
  const writes: Record<string, string> = {};

  if (params.has('gclid')) writes.gclid = params.get('gclid')!;
  if (params.has('msclkid')) writes.msclkid = params.get('msclkid')!;

  // Only persist an oppref the clickref can carry: cookie values are cut at the
  // first `;` and re-read up to the first `=`, so a rejected value stored raw
  // would come back truncated — and pass the guard — on the next page view.
  const opprefParam = params.get('oppref') || '';
  if (opprefParam && isTransportableOppref(opprefParam)) {
    writes.oppref = opprefParam;
  }

  const hasGclid = params.has('gclid') || cookies['gclid'];
  const hasMsclkid = params.has('msclkid') || cookies['msclkid'];

  if (hasGclid || hasMsclkid || params.has('gbraid') || params.has('wbraid')) {
    writes.clickref = buildClickref(pageId, params, cookies);
    return { cookies: writes, facebookTracking: false };
  }

  const oppref = opprefParam || cookies['oppref'] || '';
  const opprefClickref = oppref ? buildOpprefClickref(pageId, oppref) : null;
  if (opprefClickref) {
    writes.clickref = opprefClickref;
    return { cookies: writes, facebookTracking: false };
  }

  return { cookies: writes, facebookTracking: params.has('fbclid') };
}

/**
 * Check if clickref is valid (not a default/empty value)
 */
export function isValidClickref(clickref: string | undefined): boolean {
  if (!clickref) return false;
  if (clickref === 'seo') return false;
  if (clickref.includes('gclid_value__msclkid_value__')) return false;
  return true;
}
